// src/pages/auth/Register.tsx - ADAPTADO AL NUEVO SISTEMA
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Building, Phone, CreditCard, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  // Datos del formulario para registro de gimnasio
  const [gymData, setGymData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    gymName: '',
    ownerName: '',
    phone: '',
    cuit: ''
  });

  // Manejar cambios en el formulario de gimnasio
  const handleGymInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGymData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validar formulario de gimnasio
  const validateGymForm = () => {
    if (!gymData.email || !gymData.password || !gymData.confirmPassword ||
      !gymData.gymName || !gymData.ownerName || !gymData.phone || !gymData.cuit) {
      setError('Todos los campos son obligatorios');
      return false;
    }

    if (gymData.password !== gymData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }

    if (gymData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    return true;
  };

  // Función para registrar gimnasio
  const registerGym = async () => {
    try {
      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        gymData.email,
        gymData.password
      );

      const user = userCredential.user;

      // Enviar correo de verificación de Firebase Auth
      try {
        await sendEmailVerification(user);
        console.log('✉️ Correo de verificación enviado con éxito');
      } catch (emailErr) {
        console.error('❌ Error al enviar correo de verificación:', emailErr);
      }

      // Crear documento del gimnasio con datos de prueba limitados a 10 socios
      await setDoc(doc(db, 'gyms', user.uid), {
        name: gymData.gymName,
        owner: gymData.ownerName,
        email: gymData.email,
        phone: gymData.phone,
        cuit: gymData.cuit,
        status: 'trial',
        registrationDate: serverTimestamp(),
        trialEndsAt: new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)), // 30 días
        logo: null,
        subscriptionData: {
          plan: 'Período de Prueba',
          startDate: serverTimestamp(),
          endDate: new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)), // 30 días
          price: 0,
          paymentMethod: 'Gratuito',
          lastPayment: serverTimestamp(),
          renewalRequested: false,
          memberLimit: 10,
          tier: 'full',
          enabledModules: [
            'members', 'payments', 'settings', 'attendance',
            'daily_cash', 'products', 'classes', 'routines',
            'reports', 'qr_registration'
          ]
        }
      });

      // Crear usuario administrador en la subcolección
      await setDoc(doc(db, `gyms/${user.uid}/users`, user.uid), {
        email: gymData.email,
        name: gymData.ownerName,
        role: 'admin',
        phone: gymData.phone,
        createdAt: serverTimestamp(),
        isActive: true
      });

      return { success: true };

    } catch (error: any) {
      console.error('❌ Error en registro de gimnasio:', error);
      return { success: false, error: error.message };
    }
  };

  // Manejar envío del formulario de gimnasio
  const handleGymSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateGymForm()) {
      return;
    }

    setLoading(true);

    try {
      const result = await registerGym();

      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Error al registrar gimnasio');
      }
    } catch (err: any) {
      setError(err.message || 'Error al registrar gimnasio');
    } finally {
      setLoading(false);
    }
  };

  // Renderizar el formulario de registro de gimnasio o mensaje de éxito
  const renderGymRegistration = () => {
    if (success) {
      return (
        <div className="max-w-md w-full">
          <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-xl text-center">
            <div className="bg-green-50 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 border border-green-100">
              <CheckCircle size={40} className="text-green-600 animate-pulse" />
            </div>
            <h3 className="font-extrabold text-2xl mb-2 text-gray-800">¡Registro Exitoso!</h3>
            <p className="text-gray-600 text-sm mb-4">
              Hemos enviado un correo de verificación a:
            </p>
            <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100 mb-6">
              <span className="font-bold text-blue-950 text-base break-all">{gymData.email}</span>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 text-left text-sm text-gray-600 mb-6 space-y-2">
              <p className="font-semibold text-gray-800">Pasos para activar tu cuenta:</p>
              <ol className="list-decimal pl-5 space-y-1.5 text-xs text-gray-500">
                <li>Busca el correo de verificación enviado por el sistema.</li>
                <li>Revisa la carpeta de <strong>Spam o Correo no deseado</strong> si no lo ves en tu bandeja.</li>
                <li>Haz clic en el enlace de verificación para activar tu cuenta.</li>
                <li>Vuelve aquí e inicia sesión.</li>
              </ol>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg shadow-blue-200 active:scale-[0.98]"
            >
              Ir al Inicio de Sesión
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-md w-full">
      <div className="mb-6">
        <button
          onClick={() => navigate('/login')}
          className="text-blue-600 hover:text-blue-800 flex items-center"
        >
          <ArrowLeft size={16} className="mr-1" />
          Volver al inicio de sesión
        </button>
      </div>

      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800">Registrar Gimnasio</h2>
        <p className="text-gray-600 mt-2">Crea una cuenta para tu negocio</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md flex items-center">
          <AlertCircle size={18} className="mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md flex items-center">
          <CheckCircle size={18} className="mr-2" />
          Registro exitoso. Redirigiendo al inicio de sesión...
        </div>
      )}

      <form onSubmit={handleGymSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="gymName" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del Gimnasio *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building size={18} className="text-gray-400" />
              </div>
              <input
                id="gymName"
                name="gymName"
                type="text"
                value={gymData.gymName}
                onChange={handleGymInputChange}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nombre de tu gimnasio"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="ownerName" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del Propietario *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User size={18} className="text-gray-400" />
              </div>
              <input
                id="ownerName"
                name="ownerName"
                type="text"
                value={gymData.ownerName}
                onChange={handleGymInputChange}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Tu nombre completo"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail size={18} className="text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                value={gymData.email}
                onChange={handleGymInputChange}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="tucorreo@ejemplo.com"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone size={18} className="text-gray-400" />
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={gymData.phone}
                  onChange={handleGymInputChange}
                  className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Teléfono"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="cuit" className="block text-sm font-medium text-gray-700 mb-1">
                CUIT *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CreditCard size={18} className="text-gray-400" />
                </div>
                <input
                  id="cuit"
                  name="cuit"
                  type="text"
                  value={gymData.cuit}
                  onChange={handleGymInputChange}
                  className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="CUIT"
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock size={18} className="text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type="password"
                value={gymData.password}
                onChange={handleGymInputChange}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Mínimo 6 caracteres"
                minLength={6}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar Contraseña *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock size={18} className="text-gray-400" />
              </div>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={gymData.confirmPassword}
                onChange={handleGymInputChange}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Repite tu contraseña"
                required
              />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <button
            type="submit"
            disabled={loading || success}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center"
          >
            {loading ? (
              <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
            ) : (
              <Building size={18} className="mr-2" />
            )}
            {loading ? 'Registrando...' : 'Registrar Gimnasio'}
          </button>
        </div>
      </form>

      <div className="mt-6 text-center">
        <p className="text-gray-600">
          ¿Ya tienes una cuenta?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Inicia sesión
          </button>
        </p>
      </div>
    </div>
  );
};

  // Contenedor principal
  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Imagen lado izquierdo (50%) */}
      <div className="hidden md:block md:w-1/2 bg-blue-600">
        <div className="h-full flex items-center justify-center p-10">
          <div className="text-white text-center">
            <h1 className="text-4xl font-bold mb-4">Sistema de Gestión para Gimnasios</h1>
            <p className="text-xl mb-6">Administra socios, membresías y más</p>
            <div className="flex justify-center space-x-6">
              <div className="p-4 bg-white bg-opacity-10 rounded-lg">
                <Building size={48} className="mx-auto mb-2 text-white" />
                <p className="font-medium">Administra tu gimnasio</p>
              </div>
              <div className="p-4 bg-white bg-opacity-10 rounded-lg">
                <User size={48} className="mx-auto mb-2 text-white" />
                <p className="font-medium">Gestiona tus socios</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Formulario lado derecho (50%) */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-8">
        {renderGymRegistration()}
      </div>
    </div>
  );
};

export default Register;